import { NavLink, useLocation } from '@remix-run/react'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { Button } from '../../ui/button.tsx'
import { paths } from './paths.ts'

export function MobileMenu({ isAdmin }: { isAdmin?: boolean }) {
	const [isOpen, setIsOpen] = useState(false)
	const location = useLocation()
	const firstLinkRef = useRef<HTMLAnchorElement>(null)
	const menuButtonRef = useRef<HTMLButtonElement>(null)

	// Close menu on escape key and handle focus trap
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setIsOpen(false)
				menuButtonRef.current?.focus()
			}
		}

		const handleTabTrap = (e: KeyboardEvent) => {
			if (e.key === 'Tab' && isOpen) {
				const focusableElements = document.querySelectorAll(
					'#mobile-navigation a[href]',
				)
				const firstElement = focusableElements[0] as HTMLElement
				const lastElement = focusableElements[
					focusableElements.length - 1
				] as HTMLElement

				if (e.shiftKey && document.activeElement === firstElement) {
					e.preventDefault()
					lastElement?.focus()
				} else if (!e.shiftKey && document.activeElement === lastElement) {
					e.preventDefault()
					firstElement?.focus()
				}
			}
		}

		if (isOpen) {
			document.addEventListener('keydown', handleEscape)
			document.addEventListener('keydown', handleTabTrap)
			// Prevent body scroll when menu is open
			document.body.style.overflow = 'hidden'
			// Focus first link when menu opens
			setTimeout(() => {
				firstLinkRef.current?.focus()
			}, 100)
		} else {
			document.body.style.overflow = 'unset'
		}

		return () => {
			document.removeEventListener('keydown', handleEscape)
			document.removeEventListener('keydown', handleTabTrap)
			document.body.style.overflow = 'unset'
		}
	}, [isOpen])

	// Close menu when route changes
	useEffect(() => {
		setIsOpen(false)
	}, [location.pathname])

	const toggleMenu = () => setIsOpen(!isOpen)

	const getActiveClassName = (path: { to: string; name: string }) => {
		let isActive = false

		// Handle exact matches first
		if (location.pathname === path.to) {
			isActive = true
		}
		// Handle nested routes
		else if (
			path.to === '/work' &&
			(location.pathname === '/work' || location.pathname.startsWith('/work/'))
		) {
			isActive = true
		} else if (
			path.to === '/education' &&
			(location.pathname === '/education' ||
				location.pathname.startsWith('/education/'))
		) {
			isActive = true
		} else if (
			path.to === '/' &&
			(location.pathname.startsWith('/about') || location.pathname === '/')
		) {
			isActive = true
		}

		return `block rounded-lg px-4 py-3 text-2xl font-semibold transition-colors duration-200 ${
			isActive
				? 'bg-primary text-primary-foreground'
				: 'text-foreground hover:bg-accent hover:text-accent-foreground'
		}`
	}

	return (
		<>
			{/* Mobile Menu Button */}
			<Button
				ref={menuButtonRef}
				variant="ghost"
				size="icon"
				onClick={toggleMenu}
				className="relative z-[100]"
				aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
				aria-expanded={isOpen}
				aria-controls="mobile-navigation"
			>
				<motion.div
					animate={{ rotate: isOpen ? 180 : 0 }}
					transition={{ duration: 0.2 }}
				>
					{isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
				</motion.div>
			</Button>

			{/* Mobile Menu Overlay */}
			<AnimatePresence>
				{isOpen && (
					<div className="flex flex-col items-start justify-start">
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="fixed inset-0 z-[80] bg-background/95 backdrop-blur-sm"
							onClick={toggleMenu}
							aria-hidden="true"
						/>

						{/* Menu Content - Full screen */}
						<motion.nav
							id="mobile-navigation"
							initial={{ opacity: 0, y: -20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -20 }}
							transition={{ duration: 0.3, ease: 'easeOut' }}
							className="fixed inset-0 z-[90] flex h-screen w-screen flex-col bg-background pt-20"
							role="navigation"
							aria-label="Mobile navigation"
						>
							{/* Menu Links - Centered content */}
							<div className="justify-middle flex flex-1 flex-col items-center px-4">
								<ul
									className="w-full max-w-md space-y-6 text-center"
									role="list"
								>
									{paths.map((path, index) => (
										<li key={path.name} role="listitem">
											<motion.div
												initial={{ opacity: 0, x: -20 }}
												animate={{ opacity: 1, x: 0 }}
												transition={{
													duration: 0.3,
													delay: index * 0.1,
													ease: 'easeOut',
												}}
											>
												<NavLink
													ref={index === 0 ? firstLinkRef : undefined}
													to={path.to}
													className={() => getActiveClassName(path)}
													aria-label={`Go to ${path.name}`}
												>
													{path.name}
												</NavLink>
											</motion.div>
										</li>
									))}
									{isAdmin && (
										<li role="listitem">
											<motion.div
												initial={{ opacity: 0, x: -20 }}
												animate={{ opacity: 1, x: 0 }}
												transition={{
													duration: 0.3,
													delay: paths.length * 0.1,
													ease: 'easeOut',
												}}
											>
												<NavLink
													to="/admin"
													className={() =>
														getActiveClassName({ to: '/admin', name: 'Admin' })
													}
													aria-label="Go to Admin"
												>
													Admin
												</NavLink>
											</motion.div>
										</li>
									)}
								</ul>
							</div>
						</motion.nav>
					</div>
				)}
			</AnimatePresence>
		</>
	)
}
